import { FC, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { ReactComponent as WarningIcon } from 'assets/icons/info-circle.svg';
import { AxiosError } from 'axios';
import {
	closeConfirmModal,
	getRadabankResetPasswordInitialDataSelector,
	openConfirmModal,
	showToast,
	useAppDispatch,
	useAppSelector,
} from 'store';
import { getCurrentResetRBPasswordStep, RadabankRestorePasswordBanner } from 'components/Auth';
import { RadabankSectionHeader } from 'components/RadabankCard';
import { AuthRestoreRBPasswordStep, ERROR_MESSAGE, ROUTER_URL } from 'utils/enums';
import { getErrorMessage } from 'utils/helpers';
import { useAndroidBackButton } from 'utils/hooks/use-close-on-back-button';
import { IErrorResponse, IRadabankResetPasswordSetRequest } from 'utils/types';
import styles from './index.module.scss';

export interface RestoreRadabankPasswordStepProps {
	onSaveData?: (values: Partial<IRadabankResetPasswordSetRequest>) => void;
	initialValues: IRadabankResetPasswordSetRequest;
	onError: (error: AxiosError<IErrorResponse>) => void;
	className?: string;
}

export const AuthRestoreRadabankPassword: FC = () => {
	const dispatch = useAppDispatch();
	const history = useHistory();

	const initialFormData = useAppSelector(getRadabankResetPasswordInitialDataSelector);

	const [activeStep, setActiveStep] = useState(AuthRestoreRBPasswordStep.Secret);

	const [formData, setFormData] = useState<IRadabankResetPasswordSetRequest>(() => ({
		userId: initialFormData?.userId ?? '',
		phone: initialFormData?.phone ?? '',
		sessionid: initialFormData?.sessionid ?? '',
		secret: '',
		docnumber: '',
		birthday: '',
		code: '',
		password: '',
		password_confirm: '',
	}));

	const [bannerData, setBannerData] = useState<{ title: string; description?: string | string[] } | null>(() => {
		if (initialFormData?.error) {
			return { title: initialFormData.error };
		}
		return null;
	});

	const handleClickBack = () => {
		dispatch(
			openConfirmModal({
				title: '',
				subTitle: 'Ви впевнені, що хочете перервати процес відновлення паролю?',
				submitButtonLabel: 'Так, закрити',
				onSubmit: () => {
					dispatch(closeConfirmModal());
					history.replace(ROUTER_URL.AUTH_CHECK_PHONE);
				},
				icon: <WarningIcon className={styles.icon} />,
			})
		);
	};

	const handleSetNewData = (values: Partial<IRadabankResetPasswordSetRequest>) => {
		setFormData((prev) => ({ ...prev, ...values }));
		if (activeStep < AuthRestoreRBPasswordStep.SetNewPassword) {
			setActiveStep((prev) => prev + 1);
		} else {
			setBannerData({
				title: 'Пароль успішно змінено',
				description: 'Для продовження потрібно повторно увійти в додаток',
			});
		}
	};

	const handleRadabankRetorePasswordError = (error: AxiosError<IErrorResponse>) => {
		const message = getErrorMessage(error);

		if (message.includes(ERROR_MESSAGE.RADABANK_RESTORE_PASSWORD_NOT_MATCH)) {
			dispatch(showToast({ message }));
			return;
		}

		setBannerData({ title: message });
	};

	const CurrentStep = useMemo(() => getCurrentResetRBPasswordStep(activeStep), [activeStep]);

    useAndroidBackButton(handleClickBack);

	if (bannerData) {
		return <RadabankRestorePasswordBanner title={bannerData.title} description={bannerData.description} />;
	}

	return (
		<>
			<RadabankSectionHeader
				className={styles.header}
				title={`Крок ${activeStep + 1} з 5`}
				onClickBack={handleClickBack}
			/>

			<CurrentStep
				className={styles.content}
				onError={handleRadabankRetorePasswordError}
				initialValues={formData}
				onSaveData={handleSetNewData}
			/>
		</>
	);
};
